/**
 * Authoritative world state. The whole World object is plain JSON:
 * no classes, no Maps, no functions, no undefined-vs-missing tricks.
 * It is serialised as-is for snapshots. Only the simulation (packages/sim)
 * mutates it; everything else reads it through views (see api.ts).
 *
 * IDs are strings with a type prefix and a counter: "c12" (character),
 * "h3" (house), "t7" (title), "a5" (army), "w2" (war), "p9" (proposal),
 * "l4" (letter). New ids come from World.nextId.
 */

import type { Season } from './time';
import type { CommandResult } from './commands';
import type { CompanyChain, TradableResource } from './constants';

export type Id = string;
export type Sex = 'M' | 'F';

// ---------------------------------------------------------------- characters

export type AttributeKey =
  | 'martial'
  | 'diplomacy'
  | 'stewardship'
  | 'intrigue'
  | 'learning'
  | 'prowess';

export const ATTRIBUTE_KEYS: AttributeKey[] = [
  'martial', 'diplomacy', 'stewardship', 'intrigue', 'learning', 'prowess',
];

/** 0..20 each. */
export type Attributes = Record<AttributeKey, number>;

/**
 * Congenital genes. Each gene is a pair of alleles; 1 = trait allele, 0 = normal.
 * Expression rules live in sim/genetics.ts (see GENE_DEFS in constants.ts).
 */
export type GeneKey =
  | 'strong'
  | 'genius'
  | 'beautiful'
  | 'frail'
  | 'infertile'
  | 'giant'
  | 'dwarf'
  | 'clubfooted';

export type GenePair = [0 | 1, 0 | 1];

/** Face genes for procedural portraits: each value 0..1, fixed length FACE_GENE_COUNT. */
export const FACE_GENE_COUNT = 12;

export type EducationFocus = 'martial' | 'diplomacy' | 'stewardship' | 'intrigue' | 'learning';

export interface OpinionModifier {
  /** Character this opinion is about. */
  targetId: Id;
  value: number;
  reason: string;
  /** Game day after which the modifier is removed; null = permanent. */
  expiresDay: number | null;
}

export interface Pregnancy {
  fatherId: Id;
  dueDay: number;
  /** OPTIONAL (Phase 5 clock): absolute in-game hour the birth fires (= absHourOf(dueDay)). */
  dueHour?: number;
}

export interface Character {
  id: Id;
  firstName: string;
  sex: Sex;
  /** House this character belongs to (null = lowborn, not used for player houses). */
  houseId: Id | null;
  birthDay: number;
  deathDay: number | null;
  deathCause: string | null;
  fatherId: Id | null;
  motherId: Id | null;
  spouseId: Id | null;
  /** Ids of all children ever born. */
  childIds: Id[];
  betrothedId: Id | null;
  attributes: Attributes;
  /** 0..10; at <= 0 the character dies. */
  health: number;
  /** 0..1 base chance multiplier for conception. */
  fertility: number;
  genes: Record<GeneKey, GenePair>;
  faceGenes: number[];
  /** Trait keys, see TRAIT_DEFS in constants.ts. */
  traits: string[];
  prestige: number;
  opinionMods: OpinionModifier[];
  pregnancy: Pregnancy | null;
  education: { focus: EducationFocus; tutorId: Id | null } | null;
  /** Title this character serves as a knight (landless). Null if not a knight. */
  knightOf: Id | null;
  /** Title whose knight retinue this character serves as squire. Null if not a squire. */
  squireOf: Id | null;
  /** Holding (barony/city title id) where the character currently lives. */
  locationId: Id;
  /** If imprisoned: title id of the captor's primary title. */
  prisonerOf: Id | null;
  /** Army this character currently commands. */
  commandingArmyId: Id | null;
  /** OPTIONAL (sim/characters): true if the current marriage/betrothal is matrilineal (children join the mother's house). */
  marriageMatrilineal?: boolean;
  /** OPTIONAL (sim/characters): house the joining spouse belonged to before the current marriage (backs the marriage alliance). */
  marriageOriginHouseId?: Id;
  /** OPTIONAL (sim/characters): game day the current illness ends. */
  illUntilDay?: number;
  /** OPTIONAL (sim/ai): game day the character was assigned as a squire. */
  squireSinceDay?: number;
  /** OPTIONAL (sim/squires): the dubbed knight a personal squire serves. Null/absent for guard squires and non-squires. */
  squireMasterId?: Id | null;
  /** OPTIONAL (sim/squires): title whose Guard a guard squire serves (equal to squireOf). Null/absent otherwise. */
  guardOf?: Id | null;
  /** OPTIONAL (sim/squires): squire training progress 0..100. Defined while in training (placed or not); cleared on dubbing. */
  squireXp?: number;
  /** OPTIONAL (sim/squires): title whose Guard this active knight captains (always equals knightOf when set). */
  guardCaptainOf?: Id | null;
  /** OPTIONAL (sim/skills): innate aptitude per skill, 0..10. `attributes[k]` = min(20, aptitude[k] + training rank). Backfilled by normalizeWorld. */
  aptitude?: Attributes;
  /** OPTIONAL (sim/skills): accumulated training XP per skill (rank r needs 50 x r^2 total, max rank 10). */
  skillXp?: Attributes;
  /** OPTIONAL (sim/skills): lifestyle focus skill of an adult (+50 % XP in it). */
  focus?: AttributeKey;
  /** OPTIONAL (sim/skills): game day the focus was last changed; the +50 % bonus is suspended for one year after. Absent = no penalty. */
  focusSinceDay?: number;
  /** OPTIONAL (sim/perks): perk keys chosen per skill, in tier order (index 0 = rank 3, 1 = rank 6, 2 = rank 9). */
  perks?: Partial<Record<AttributeKey, string[]>>;
  /** OPTIONAL (sim/perks): personal renown (never spent; backfilled from `prestige`). Absent = `prestige`. */
  renown?: number;
  /** OPTIONAL (sim/perks): true once the Undying perk has saved this character. */
  undyingUsed?: boolean;
}

// -------------------------------------------------------------------- houses

export interface Resources {
  gold: number;
  food: number;
  timber: number;
  stone: number;
  iron: number;
  horses: number;
  wool: number;
  manpower: number;
}

export const RESOURCE_KEYS: (keyof Resources)[] = ['gold', 'food', 'timber', 'stone', 'iron', 'horses', 'wool', 'manpower'];

export interface StandingOrders {
  /** Marshal raises levies automatically when an enemy army enters our land. */
  autoRaiseLevies: boolean;
  /** Answer to calls to arms from liege/allies. */
  callToArms: 'join' | 'refuse';
  /** Armies retreat to the nearest own castle if outnumbered by this ratio (e.g. 1.5). 0 = never. */
  retreatRatio: number;
  /** Incoming marriage proposals for members of the house. */
  marriageProposals: 'decline' | 'accept_reasonable';
  /** OPTIONAL (absent = true): knights of this house serving a lord ride with the lord's armies. */
  rideWithLord?: boolean;
}

export type HouseControl =
  /** A human player is in control. */
  | 'player'
  /** Player inactive; AI caretaker plays defensively until the player returns. */
  | 'caretaker'
  /** AI house (original AI or a player house past the game-over threshold). */
  | 'ai';

export interface House {
  id: Id;
  name: string;
  /** Account that owns this house; null for AI houses. Kept after game over for history. */
  ownerAccountId: string | null;
  control: HouseControl;
  /** True if this house was ever played by a human (for "former player house" label). */
  wasPlayer: boolean;
  foundedDay: number;
  /** Head of house character id (null once extinct). */
  headId: Id | null;
  extinct: boolean;
  extinctDay: number | null;
  resources: Resources;
  prestige: number;
  standingOrders: StandingOrders;
  /** Game day of the player's last session (only meaningful for player-controlled houses). */
  lastActiveDay: number;
  /** Coat of arms seed for procedural heraldry. */
  heraldrySeed: number;
  /** OPTIONAL (sim/houses): last inactivity event already announced (warnings/stage changes), reset by markActive. */
  warnedStage?: string;
  /** OPTIONAL (design pass 1, ambitions): the active ambition; `milestone` = milestones already reached (0..2; at 3 it completes). `bondHouseId` = house married into (Bonds of blood). */
  ambition?: { key: AmbitionKey; milestone: number; chosenDay: number; bondHouseId?: Id };
  /** OPTIONAL: completed ambition keys (finishing any unlocks "Rise of a dynasty"). */
  ambitionsDone?: AmbitionKey[];
  /** OPTIONAL: abandoned ambition key -> game day it may be chosen again. */
  ambitionCooldowns?: Partial<Record<AmbitionKey, number>>;
  /** OPTIONAL: +10 % skill XP for every member until this game day (ambition reward). */
  xpBoostUntil?: number;
  /** OPTIONAL: +10 % squire training XP for the house until this game day (ambition reward). */
  squireXpBoostUntil?: number;
  /** OPTIONAL: game day of the last petition_liege sent by a member (ambition milestone). */
  petitionedDay?: number;
}

export type AmbitionKey =
  | 'seat_at_the_table' | 'knights_of_renown' | 'land_of_our_own' | 'merchant_house' | 'bonds_of_blood' | 'rise_of_a_dynasty';

// -------------------------------------------------------------------- titles

export type TitleRank = 'kingdom' | 'duchy' | 'county' | 'barony' | 'city';

export const RANK_ORDER: Record<TitleRank, number> = {
  kingdom: 5, duchy: 4, county: 3, city: 2, barony: 1,
};

export type SuccessionLaw = 'primogeniture' | 'gavelkind' | 'elective' | 'seniority';
export type GenderLaw = 'male_preference' | 'equal' | 'female_preference';

export interface RealmLaws {
  succession: SuccessionLaw;
  gender: GenderLaw;
  /** 0 = autonomous vassals .. 3 = absolute. Gates revocation and internal-war ban. */
  crownAuthority: 0 | 1 | 2 | 3;
  /** Maximum tax rate any liege in the realm may demand from a vassal (0..1). */
  taxCap: number;
  /** Internal wars forbidden (needs crownAuthority >= 2). */
  internalWarsBanned: boolean;
}

export type CouncilSeat =
  | 'chancellor'
  | 'steward'
  | 'marshal'
  | 'spymaster'
  | 'physician'
  | 'treasurer'
  | 'captain';

/** Which council seats each rank has. */
export const COUNCIL_SEATS: Record<TitleRank, CouncilSeat[]> = {
  kingdom: ['chancellor', 'steward', 'marshal', 'spymaster', 'physician'],
  duchy: ['chancellor', 'steward', 'marshal', 'spymaster'],
  county: ['steward', 'marshal', 'spymaster'],
  barony: ['steward', 'marshal'],
  city: ['treasurer', 'captain'],
};

export type Terrain = 'plains' | 'farmland' | 'hills' | 'forest' | 'mountains' | 'marsh' | 'coast';

export type BuildingKey =
  | 'farmland'
  | 'mine'
  | 'lumber_camp'
  | 'quarry'
  | 'stables'
  | 'barracks'
  | 'walls'
  | 'market';

export interface Construction {
  building: BuildingKey;
  /** Target level after completion. */
  level: number;
  doneDay: number;
  /** OPTIONAL (Phase 5 clock): absolute in-game hour construction completes (kept in sync with doneDay). */
  doneHour?: number;
}

/** City districts (Design pass 1, step 3). Harbour needs coast terrain. */
export type DistrictKey = 'market_quarter' | 'craft_quarter' | 'harbour' | 'guildhall' | 'watch_house' | 'cathedral';

/** A development project in progress (one per holding, separate from the one-construction rule). */
export interface DevProject {
  /** Development level reached on completion. */
  target: number;
  startDay: number;
  doneDay: number;
  /** Absolute in-game hour the project completes (kept in sync with doneDay). */
  doneHour: number;
}

/** A district under construction in a city (one at a time, paid from the city treasury). */
export interface DistrictProject {
  district: DistrictKey;
  doneDay: number;
  doneHour: number;
}

/** Physical place on the map. Only baronies and cities have one. */
export interface Holding {
  x: number;
  y: number;
  terrain: Terrain;
  /** 1..10 */
  development: number;
  /** Building levels (0 = not built). */
  buildings: Partial<Record<BuildingKey, number>>;
  construction: Construction | null;
  /** Soldiers defending the holding. */
  garrison: number;
  /** Men currently available to be raised as levies. Regenerates monthly. */
  levyPool: number;
  /** Occupied by army of this war side (army id of occupier), null if not occupied. */
  occupiedBy: Id | null;
  /** OPTIONAL (sim/holdings, Design pass 1): inhabitants, 50..5000. Backfilled from development by normalizeWorld. */
  population?: number;
  /** OPTIONAL (sim/holdings): 0..100. */
  prosperity?: number;
  /** OPTIONAL (sim/holdings): consecutive months with prosperity below 20 (12 costs a development level). */
  lowProsperityMonths?: number;
  /** OPTIONAL (sim/holdings): development project in progress. */
  devProject?: DevProject | null;
  /** OPTIONAL (cities): treasury gold (tariffs, licence and market fees; 10 % goes to the mayor's house as salary). */
  treasury?: number;
  /** OPTIONAL (cities): running total of trade, 1 per 100 gold traded. */
  commerce?: number;
  /** OPTIONAL (cities): commerce earned last month (drives the prosperity bonus). */
  commerceGain?: number;
  /** OPTIONAL (cities): gold of trade not yet turned into commerce (< 100). */
  tradeCarry?: number;
  /** OPTIONAL (cities): districts built. */
  districts?: DistrictKey[];
  /** OPTIONAL (cities): district under construction. */
  districtProject?: DistrictProject | null;
}

/**
 * How the current holder came to hold a title. Mirrors the sim's TransferReason values
 * ('inheritance' | 'grant' | 'revocation' | 'conquest' | 'election' | 'usurpation' | 'vacancy'),
 * plus 'creation' = the initial assignment at world generation and 'founding' = a frontier settlement or title created by its holder.
 */
export type TitleGainReason =
  | 'inheritance' | 'grant' | 'revocation' | 'conquest' | 'election' | 'usurpation' | 'vacancy' | 'creation' | 'founding';

/** One holdership of a title in its tenure ledger. endDay null = the current holder. */
export interface TitleTenure {
  holderId: Id;
  houseId: Id | null;
  startDay: number;
  endDay: number | null;
  reason: TitleGainReason;
}

export interface Title {
  id: Id;
  name: string;
  rank: TitleRank;
  /** Current holder (character id) or null if vacant. */
  holderId: Id | null;
  /** De jure parent title (kingdom for duchy, duchy for county, county for barony/city). */
  deJureParentId: Id | null;
  /**
   * De facto liege title: whose vassal the holder of this title is.
   * Normally equals deJureParentId; differs after allegiance switches.
   * Kingdoms have null.
   */
  liegeTitleId: Id | null;
  /** Kingdom only: realm laws. */
  laws: RealmLaws | null;
  /** Council seat -> character id. Keys = COUNCIL_SEATS[rank]. */
  council: Partial<Record<CouncilSeat, Id | null>>;
  /** Tax rate this title's holder demands from its direct vassals (0..laws.taxCap). */
  vassalTaxRate: number;
  /** Share of each direct vassal's levies that must answer a call to arms (0..1). */
  vassalLevyRate: number;
  /** Map position + economy for baronies and cities, null for higher titles. */
  holding: Holding | null;
  /** For counties/duchies/kingdoms: capital holding (barony title id). */
  capitalId: Id | null;
  /** Display colour (hex) for map rendering of realms. */
  color: string;
  /** Characters with a claim on this title. */
  claimantIds: Id[];
  /** Elective succession: elector id -> candidate id. */
  votes: Record<Id, Id>;
  /** City titles only: the day of the next Lord Mayor election (civic term elections, law-independent). */
  nextElectionDay?: number;
  /** Bounded ledger of past holderships, newest last (cap 50). Optional: older snapshots lack it;
   *  normalizeWorld backfills an empty ledger for them. Only transferTitle / worldgen write it. */
  tenures?: TitleTenure[];
  /** OPTIONAL (frontier): set on every title of a frontier region (sites, county/duchy/kingdom placeholders). See FrontierMark. */
  frontier?: FrontierMark;
}

/**
 * Frontier bookkeeping carried by the titles of a frontier region (Design pass 2, step 6).
 *  - `site` / `city_site`: an unheld barony / city holding. `settled:false` = dormant (no economy, no market) until a
 *    settler's order completes; then `settled:true`, `founderHouseId` and `protectedUntilDay` are set.
 *  - `county` / `duchy` / `kingdom`: placeholder titles with `uncreated:true` (no holder, no laws) until `create_title`.
 *  - `charter`: the settler's charter (on the settled site and on titles the settler creates later).
 */
export type FrontierKind = 'site' | 'city_site' | 'county' | 'duchy' | 'kingdom';
export interface FrontierCharter {
  patronCharId: Id;
  patronHouseId: Id | null;
  /** The patron's primary title when the charter was sealed: the liege of the settler. */
  patronTitleId: Id;
  /** Last day of the charter period (tax/levy caps and no revocation until then). */
  untilDay: number;
}
export interface FrontierSettling {
  charId: Id;
  houseId: Id;
  startDay: number;
  doneHour: number;
  doneDay: number;
  /** Gold/timber/stone the patron paid (refunded if the settlement fails). */
  patronPaid?: { houseId: Id; gold: number; timber: number; stone: number };
  /** What the settling house paid (refunded if the settlement fails). */
  paid: { gold: number; timber: number; stone: number; manpower: number };
  charter?: FrontierCharter;
}
export interface FrontierMark {
  regionId: Id;
  kind: FrontierKind;
  /** Sites only: true once a settler holds it. */
  settled?: boolean;
  /** County/duchy/kingdom placeholders only: true until create_title fills in the holder. */
  uncreated?: boolean;
  /** Sites: the order in progress (one year). */
  settling?: FrontierSettling | null;
  /** Sites: the house that founded the settlement (the one-site-until-development-2 rule). */
  founderHouseId?: Id | null;
  /** Sites: no war may be fought against this holding before this day. */
  protectedUntilDay?: number;
  charter?: FrontierCharter | null;
}

/** One frontier region: a kingdom's worth of land opened at the map edge. Titles are in world.titles, flagged `frontier`. */
export interface FrontierRegion {
  id: Id;
  openDay: number;
  /** Old-kingdom lords may not declare war into the region before this day (openDay + FRONTIER_WAR_TRUCE_DAYS). */
  truceEndDay: number;
  /** The kingdom whose edge the region was opened next to. */
  nearKingdomId: Id;
  kingdomId: Id;
  duchyIds: Id[];
  countyIds: Id[];
  /** Barony sites and city sites (cities last). */
  siteIds: Id[];
  /** Map rectangle the region occupies. */
  bounds: { x: number; y: number; w: number; h: number };
}
export interface FrontierState {
  regions: FrontierRegion[];
  /** Day the world's human share first reached FRONTIER_SHARE_THRESHOLD in an unbroken run; null while below. */
  shareSinceDay: number | null;
  /** Day the last region opened (houses founded before it do not count towards the next opening). */
  lastOpenDay: number | null;
}

export interface Road {
  a: Id;
  b: Id;
  /** Travel time for an average army in game days. */
  days: number;
}

// ------------------------------------------------------------------ military

export interface Troops {
  levy: number;
  spearmen: number;
  archers: number;
  lightCavalry: number;
  heavyCavalry: number;
}

export const TROOP_KEYS: (keyof Troops)[] = ['levy', 'spearmen', 'archers', 'lightCavalry', 'heavyCavalry'];

export type Formation = 'line' | 'wedge' | 'shield_wall' | 'flanking';
export type Posture = 'hold' | 'defend_region' | 'aggressive';

export interface Army {
  id: Id;
  houseId: Id;
  /** Owner = holder whose levies/men these are. */
  ownerId: Id;
  commanderId: Id | null;
  troops: Troops;
  /** Morale 0..1. */
  morale: number;
  /** Current holding id. */
  locationId: Id;
  /** Remaining holding ids to travel through, in order (excluding current). */
  path: Id[];
  /** Game day when the army reaches path[0]; null if not moving. */
  nextArrivalDay: number | null;
  /** OPTIONAL (Phase 5 clock): absolute in-game hour the next path edge completes; null/undefined = not marching. Kept in sync with nextArrivalDay. */
  nextArrivalHour?: number | null;
  formation: Formation;
  posture: Posture;
  /** Siege in progress at current location. */
  siege: {
    holdingId: Id;
    progress: number;
    /** OPTIONAL (Phase 5 clock): absolute hour this siege is scheduled to complete (re-derived daily). */
    doneHour?: number;
    /** OPTIONAL (Phase 5 clock): absolute hour at which the siege started (rate-model reference). */
    startHour?: number;
  } | null;
  /** OPTIONAL (sim/military): knights riding with this army (refreshed monthly; validated again at every battle). */
  knightIds?: Id[];
  /** OPTIONAL (sim/military): holding whose siege start was already chronicled for this army (cleared when it leaves). */
  siegeLogged?: Id;
  /** Day the army was raised (levies cost upkeep while raised). */
  raisedDay: number;
}

export type WarGoalType = 'seize_title' | 'tribute' | 'humiliate' | 'independence' | 'depose';

export interface WarGoal {
  type: WarGoalType;
  /** Target title for seize_title/independence/depose. */
  titleId: Id | null;
}

export interface War {
  id: Id;
  name: string;
  attackerId: Id;
  defenderId: Id;
  attackerAllyIds: Id[];
  defenderAllyIds: Id[];
  goal: WarGoal;
  startDay: number;
  /** -100 (defender wins) .. +100 (attacker wins). */
  score: number;
  endedDay: number | null;
  outcome: 'attacker_won' | 'defender_won' | 'white_peace' | null;
  /** True if attacker had a claim (cheaper). */
  justified: boolean;
  /** OPTIONAL (Round B2): the vassal faction that declared this war. */
  factionId?: Id;
  /** OPTIONAL (design pass 1, muster): first day the attacker's side may enter, fight in or besiege the defender side's land. Absent on legacy wars = no muster. */
  musterEndsDay?: number;
}

export interface Truce {
  aId: Id;
  bId: Id;
  untilDay: number;
}

// ------------------------------------------------------------- diplomacy

export type ProposalKind = 'marriage' | 'alliance' | 'non_aggression' | 'service' | 'peace' | 'betrothal' | 'faction_demand' | 'wardship' | 'squire' | 'charter';

/** Alliance contract length in years. */
export type AllianceDurationYears = 5 | 10 | 20;

export interface Proposal {
  id: Id;
  kind: ProposalKind;
  fromId: Id; // character who proposes (usually a head of house or ruler)
  toId: Id; // character who must answer (head of the other house / ruler)
  /** marriage/betrothal: the two characters; service: knight + title; squire: squire (A) + knight (B); peace: war id. */
  subjectAId: Id | null;
  subjectBId: Id | null;
  matrilineal: boolean;
  dowry: number;
  warId: Id | null;
  titleId: Id | null;
  /** peace proposals only: what the sender offers (see sim/wars.ts). */
  terms?: 'white_peace' | 'surrender';
  /** OPTIONAL (Round B2): faction_demand proposals — the demanding faction. */
  factionId?: Id;
  /** OPTIONAL: alliance proposals only, contract length in years (snapshot compatibility). */
  durationYears?: AllianceDurationYears;
  /** OPTIONAL (Round C2): set when a favour (hook) was called in on this proposal — the answer is coerced to yes. */
  hookId?: Id;
  /** OPTIONAL (Round C3): wardship proposals — the child to be fostered in the recipient house. */
  childId?: Id;
  /** OPTIONAL (frontier): charter proposals - set once settle_site has used the accepted charter. */
  charterUsed?: boolean;
  sentDay: number;
  /** Day the letter arrives at the recipient (in-world delivery delay). */
  arriveDay: number;
  expiresDay: number;
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'invalid';
}

export interface Alliance {
  aHouseId: Id;
  bHouseId: Id;
  untilDay: number | null; // null = while the marriage lasts
  viaMarriage: boolean;
  /** OPTIONAL (Round C1): 'non_aggression' is a non-aggression pact (no call to arms); undefined = formal alliance (snapshot back-compat). */
  kind?: 'alliance' | 'non_aggression';
}

export interface Letter {
  id: Id;
  fromId: Id;
  toId: Id;
  text: string;
  sentDay: number;
  arriveDay: number;
  read: boolean;
}

/** A favour owed (Round C2): the holder may call it in once to force the target's yes (GDD: force a yes). */
export interface Hook {
  id: Id;
  /** Who may call it in (character id). */
  holderId: Id;
  /** Who owes the favour (character id). */
  targetId: Id;
  createdDay: number;
  /** Day the favour was called in; one-time use (null = unspent). */
  spentDay: number | null;
  source: 'rumours' | 'gift' | 'blackmail';
}

export type FactionType = 'lower_taxes' | 'claimant' | 'independence';

/** Vassals unhappy with their liege banding together (Round B1: model only — demands arrive in B2). */
export interface Faction {
  id: Id;
  /** Title whose vassals are factioning (the liege title). */
  liegeTitleId: Id;
  type: FactionType;
  /** Primary title ids of member vassals. */
  memberTitleIds: Id[];
  /** Founder/leader's primary title id; re-elected deterministically if lost. */
  leaderTitleId: Id;
  /** For 'claimant' factions: the character id backed against the liege. */
  claimantId: Id | null;
  formedDay: number;
  /** Strength ratio met — ready factions send their demand (Round B2). */
  ready: boolean;
  /** OPTIONAL (Round B2): the pending faction_demand proposal sent to the liege. */
  demandProposalId?: Id;
  /** OPTIONAL (Round B2): the faction war declared after a refused/expired demand. */
  warId?: Id;
}

/** A long-running plot by a house against a single character. Phase 3. */
export type SchemeType = 'murder' | 'abduction' | 'fabricate_claim' | 'spread_rumours' | 'steal_funds' | 'befriend';
export interface Scheme {
  id: Id;
  type: SchemeType;
  /** Scheming house. */
  houseId: Id;
  /** Character who masterminds the plot (fixed at start; death ends the scheme). */
  leaderId: Id;
  /** Victim character. */
  targetId: Id;
  /** For fabricate_claim: the title the claim is forged against. Absent for murder/abduction. */
  targetTitleId?: Id;
  startedDay: number;
  /** 0..SCHEMES[type].progressNeeded. */
  progress: number;
  /** Known to the target's house. */
  discovered: boolean;
}

// ------------------------------------------------------------- merchant path (Phase 5)

/** A limit order on a city market; buy orders hold gold escrow, sell orders hold resource escrow. */
export interface MarketOrder {
  id: Id;
  houseId: Id;
  side: 'buy' | 'sell';
  resource: TradableResource;
  quantity: number;
  remaining: number;
  unitPrice: number;
  placedDay: number;
  expiresDay: number;
}

/** Per-city market: maker bookkeeping, tariff and the live order book. */
export interface Market {
  tariffRate: number;
  orders: MarketOrder[];
  makerGold: number;
  makerStock: Partial<Record<TradableResource, number>>;
  lastPrices: Partial<Record<TradableResource, number>>;
}

/** A production company in a city, owned by a house. */
export interface Company {
  id: Id;
  name: string;
  cityId: Id;
  ownerId: Id;
  licenceLevel: 1 | 2 | 3;
  managerId: Id | null;
  chain: CompanyChain;
  foundedDay: number;
}

/** Per-city guild: houseId -> xp (derived ranks, nothing stored). */
export interface Guild {
  xp: Record<string, number>;
}

/** A loan from one house to another. */
export interface Loan {
  id: Id;
  lenderHouseId: Id;
  borrowerHouseId: Id;
  principal: number;
  outstanding: number;
  annualRate: number;
  issuedDay: number;
  lastAccrualDay: number;
  missed: number;
  defaulted: boolean;
}

// ------------------------------------------------------------- chronicle

export type ChronicleKind =
  | 'birth'
  | 'death'
  | 'marriage'
  | 'coming_of_age'
  | 'succession'
  | 'title_granted'
  | 'title_revoked'
  | 'title_lost'
  | 'war_declared'
  | 'war_ended'
  | 'battle'
  | 'siege'
  | 'construction'
  | 'council'
  | 'law'
  | 'house_founded'
  | 'house_extinct'
  | 'inactivity'
  | 'event'
  | 'proposal'
  /** Letters arrived (private: only shown to the houses concerned). */
  | 'letter'
  | 'knighted'
  /** A character became a squire in a title's knight retinue. */
  | 'squire'
  /** Schemes (Phase 3; private: only shown to the houses concerned). */
  | 'scheme_started'
  | 'scheme_discovered'
  | 'scheme_resolved'
  /** A vassal faction formed against a liege (public — GDD "notify: faction formed against you"). */
  | 'faction_formed'
  /** A ready faction sent its demand to the liege (public). */
  | 'faction_demand'
  /** A refused/expired faction demand escalated to a faction war (public). */
  | 'faction_war'
  /** A faction disbanded after its demand was granted or crushed (public). */
  | 'faction_disbanded'
  /** A hook (favour) changed hands (private: only the two houses concerned). */
  | 'hook_gained'
  /** Round C3: a child was sent to be raised by another house (wardship — public; ends silently). */
  | 'wardship'
  /** An ambition milestone was reached or an ambition completed (private to the house). */
  | 'ambition'
  /** A legendary (renown 1000+) character died: hall of fame (public). */
  | 'legend';

export interface ChronicleEntry {
  id: number;
  day: number;
  /** OPTIONAL (Phase 5 clock): in-game hour of the entry (absent = day boundary, hour 0). */
  hour?: number;
  kind: ChronicleKind;
  text: string;
  /** Houses this entry concerns (for per-house feeds and push notifications). */
  houseIds: Id[];
  characterIds: Id[];
  /** True if this should trigger a push notification to involved player houses. */
  important: boolean;
}

// ------------------------------------------------------------- world

/** One caravan: goods bought at a market city, in transit to the buyer house's seat. */
export interface Delivery {
  id: Id;
  houseId: Id;
  resource: TradableResource;
  quantity: number;
  /** City title the goods travel from. */
  fromCityId: Id;
  /** Game day the goods arrive (settle into house stock on the daily tick). */
  arriveDay: number;
}

export interface WorldMeta {
  /** Real timestamp (ms) at which game day 0 started. Set by the server after pre-simulation. */
  epochMs: number;
  seed: number;
  kingdomId: Id;
  /** Unused since the per-kingdom awakening (kept in the type for snapshot compatibility). */
  targetHumanHouses: number;
  /** OPTIONAL (sim/houses): kingdoms that have seen a human house seated; they never go back to sleep. */
  awakeKingdomIds?: Id[];
  /** OPTIONAL (sim/perks): characters who died Legendary (renown 1000+). */
  hallOfFame?: { id: Id; name: string; renown: number; deathDay: number }[];
  version: number;
  /** OPTIONAL (sim/worldgen): map size in map units; absent in older worlds (normalizeWorld backfills 1000 x 700). */
  mapWidth?: number;
  mapHeight?: number;
  /** OPTIONAL (sim/worldgen): number of kingdoms the world was created with; absent = counted from the titles. */
  kingdomCount?: number;
  /**
   * Server bookkeeping (the sim never reads it): results of recently applied player commands, stored inside every
   * snapshot so that a command is applied exactly once even if the process dies right after a snapshot.
   */
  appliedCommands?: Record<string, { result: CommandResult; appliedAt: number }>;
}

export interface World {
  meta: WorldMeta;
  /** Last fully processed game day. */
  day: number;
  /** OPTIONAL (Phase 5 clock): scheduled events, ordered; wake-up calls for exact in-game hour resolution. Absent on old snapshots. */
  events?: ScheduledEvent[];
  /** Seeded RNG state (mulberry32). Only sim/rng.ts touches it. */
  rngState: number;
  nextId: number;
  nextChronicleId: number;
  characters: Record<Id, Character>;
  houses: Record<Id, House>;
  titles: Record<Id, Title>;
  roads: Road[];
  armies: Record<Id, Army>;
  wars: Record<Id, War>;
  truces: Truce[];
  proposals: Record<Id, Proposal>;
  alliances: Alliance[];
  letters: Record<Id, Letter>;
  /** OPTIONAL for snapshot compatibility (Phase 3): running schemes. */
  schemes?: Record<Id, Scheme>;
  /** OPTIONAL (Phase 5 merchant path): per-city markets, keyed by city title id. */
  market?: Record<Id, Market>;
  /** OPTIONAL (Phase 5 merchant path): companies, keyed by company id. */
  companies?: Record<Id, Company>;
  /** OPTIONAL (Phase 5 merchant path): per-city guilds, keyed by city title id. */
  guilds?: Record<Id, Guild>;
  /** OPTIONAL (Phase 5 merchant path): loans between houses. */
  loans?: Loan[];
  /** OPTIONAL (caravans): goods in transit from a market city to the buyer house's seat, keyed by delivery id. */
  deliveries?: Record<Id, Delivery>;
  /** OPTIONAL (caravans): counter for delivery ids (d1, d2, ...); backfilled by normalizeWorld. */
  nextDeliveryId?: number;
  /** OPTIONAL (Round B1): vassal factions; backfilled to [] by normalizeWorld. */
  factions?: Faction[];
  /** OPTIONAL (Round B1): counter for faction ids (fa1, fa2, ...); backfilled by normalizeWorld. */
  nextFactionId?: number;
  /** OPTIONAL (Round C2): favours owed (hooks); backfilled to [] by normalizeWorld. */
  hooks?: Hook[];
  /** OPTIONAL (Round C2): counter for hook ids (hk1, hk2, ...); backfilled by normalizeWorld. */
  nextHookId?: number;
  /** OPTIONAL (frontier): frontier regions and the opening trackers; absent until the first monthly check that needs them. */
  frontier?: FrontierState;
  /** Most recent entries, capped (see CHRONICLE_CAP). Older ones are flushed to storage by the server. */
  chronicle: ChronicleEntry[];
}

// ------------------------------------------------------------- scheduled events

/** A scheduled wake-up call: resolves at exact in-game hour `atHour`. ref = armyId | characterId | holdingId. */
export interface ScheduledEvent {
  kind: 'march' | 'birth' | 'construction' | 'siege' | 'development' | 'district' | 'settlement';
  ref: Id;
  atHour: number;
}

export const CHRONICLE_CAP = 3000;

export type { Season };
